import { api } from '@api'
import { useLocation, useNavigate } from '@solidjs/router'
import { setUser } from '@stateApp'
import {
  ErrorBoundary,
  type ParentProps,
  Show,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js'
import { useService } from 'solid-services'
import { isProd } from 'src'
import { ALLOWED_EMAILS } from 'src/constants/allowedEmails'

// Resource Imports
import { useChannelsReads } from '@state/channelReads/state'
import { useEvents } from '@state/events'
import { useMessageMeta } from '@state/messageMeta'
import { useSubscription } from '@state/subscription'
import { useAuth, useChannels, useNotifications } from '@stateApp'

// Component Imports
import { Splash } from '../layout/Splash'

export const RouteGuard = (props: ParentProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  // 1. Initialize all required resources here
  const { profileResource } = useService(useAuth)()
  const { channelsResource } = useService(useChannels)()
  const { notificationsQuery } = useService(useNotifications)()
  const { messageMetaResource } = useService(useMessageMeta)()
  const { channelReadsQuery } = useService(useChannelsReads)()
  const { subscription } = useService(useSubscription)()
  const { events } = useService(useEvents)()

  // 2. Track Auth separately from overall app readiness
  const [authChecked, setAuthChecked] = createSignal(false)

  // 3. One-way latch to prevent the splash screen from showing again after initial boot
  const [hasBooted, setHasBooted] = createSignal(false)

  onMount(async () => {
    // Set up the auth state listener
    const { data: { subscription: authSub } } = api().auth.onAuthStateChange(async (event, session) => {
      console.log(`🔒 RouteGuard: Auth Event ${event}`)

      const e = event as string
      if (e === 'SIGNED_OUT') {
        setUser(undefined)
        navigate('/auth/login', {
          replace: true,
          state: { loginRedirect: location.pathname },
        })
      } else if (e === 'TOKEN_REFRESH_FAILED') {
        await handleLogout()
      } else if (session) {
        // --- TEMPORARY WHITELIST START ---
        if (isProd && !ALLOWED_EMAILS.includes(session.user.email?.toLowerCase() || '')) {
          console.log('🔒 RouteGuard: User not whitelisted. Forcing logout.')
          await handleLogout()
          return
        }
        // --- TEMPORARY WHITELIST END ---

        setUser(session)
        setAuthChecked(true)
      }
    })

    // Perform initial session check
    try {
      const { data, error } = await api().auth.getSession()

      if (error) {
        if (error.code === 'refresh_token_not_found') {
          await handleLogout()
          return
        }
        throw error
      }

      if (data.session) {
        // --- TEMPORARY WHITELIST START ---
        if (isProd && !ALLOWED_EMAILS.includes(data.session.user.email?.toLowerCase() || '')) {
          console.log('🔒 RouteGuard: User not whitelisted. Forcing logout.')
          await handleLogout()
          return
        }
        // --- TEMPORARY WHITELIST END ---

        console.log('🔒 RouteGuard: User session found')
        setUser(data.session)
        setAuthChecked(true)
      } else {
        await handleLogout()
      }
    } catch (e) {
      console.error('🔒 RouteGuard: Session check failed', e)
      await handleLogout()
    }

    onCleanup(() => {
      authSub.unsubscribe()
    })
  })

  const handleLogout = async () => {
    try {
      await api().auth.signOut({ scope: 'local' })
    } catch (e) {
      console.warn('Error during local signout', e)
    }

    navigate('/auth/login', {
      replace: true,
      state: { loginRedirect: location.pathname },
    })
  }

  // 4. Reactive derived state to check if everything is fully loaded
  const isAppReady = () => {
    return (
      authChecked() &&
      messageMetaResource.state === 'ready' &&
      notificationsQuery.isSuccess &&
      channelReadsQuery.isSuccess &&
      channelsResource.state === 'ready' &&
      profileResource.state === 'ready' &&
      subscription.isPending === false &&
      events.isPending === false
    )
  }

  // 5. The Latch: As soon as the app is ready once, lock it in so the splash screen goes away forever
  createEffect(() => {
    if (isAppReady() && !hasBooted()) {
      setHasBooted(true)
    }
  })

  return (
    <ErrorBoundary
      fallback={(error) => {
        console.error(error)
        return error
      }}
    >
      <Show when={hasBooted()} fallback={<Splash />}>
        {props.children}
      </Show>
    </ErrorBoundary>
  )
}
